import { useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';

import { elapsedMinutesSince, useNow } from '../../timesheets/useNow.js';
import { useTimesheetsQuery } from '../../timesheets/useTimesheetsQuery.js';

import { HOME_CARD_LINK, HOME_CARD_ROW, HomeCard, homeCardStatus } from './HomeCard.js';

/** Six names before the card would need its own scrollbar. Past that it says how many are left. */
const MAX_ROWS = 6;

/**
 * "On the clock now" — who is punched in across the tenant, right now.
 *
 * NO NEW ENDPOINT. The week-grid response already carries `activeClocks` for the whole tenant and
 * refetches every 30 seconds, so this is the same request the crew card makes and the Timesheets
 * screen makes. The elapsed figures tick client-side in between, so the card reads as live without
 * polling harder than the information changes.
 *
 * READ ONLY, deliberately. The Timesheets screen offers "edit the start time" and "close it out" on
 * the same rows. Those are corrections to a pay record and they belong on the screen built to make
 * them, behind the confirmation and the reason field that screen already asks for. Home says who is
 * working; it does not edit anybody's hours.
 */
export function DispatcherOnClock() {
  const { t } = useTranslation();
  const now = useNow();
  const query = useTimesheetsQuery({});

  const clocks = query.data?.activeClocks ?? [];
  const shown = clocks.slice(0, MAX_ROWS);

  const elapsedLabel = (clockInAt: string): string | null => {
    const minutes = elapsedMinutesSince(clockInAt, now);
    if (minutes === null || minutes < 1) return null;
    return minutes >= 60
      ? t('timesheets:myClock.elapsedHm', {
          hours: Math.floor(minutes / 60),
          minutes: minutes % 60,
        })
      : t('timesheets:myClock.elapsedM', { minutes: minutes % 60 });
  };

  return (
    <HomeCard
      headingId="dispatcher-onclock-heading"
      title={t('home:dispatcher.onClock.heading')}
      action={
        <Link to="/timesheets" search={{}} className={HOME_CARD_LINK}>
          {t('home:dispatcher.onClock.openTimesheets')}
        </Link>
      }
      status={homeCardStatus(query, shown.length)}
      errorText={t('home:dispatcher.onClock.unavailable')}
      emptyText={t('home:dispatcher.onClock.empty')}
      onRetry={() => void query.refetch()}
    >
      <ul className="mt-3 flex flex-col gap-2">
        {shown.map((clock) => (
          <li key={clock.id} className={HOME_CARD_ROW}>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
              <span className="font-medium text-foreground text-sm">{clock.employeeName}</span>
              <span className="font-mono text-muted-foreground text-xs tabular-nums">
                {elapsedLabel(clock.clockInAt)}
              </span>
            </div>
            <span className="text-muted-foreground text-xs">{clock.jobName}</span>
          </li>
        ))}
        {clocks.length > shown.length ? (
          <li className="text-muted-foreground text-xs">
            {t('home:dispatcher.onClock.more', { count: clocks.length - shown.length })}
          </li>
        ) : null}
      </ul>
    </HomeCard>
  );
}
